// Global activity log hook — consumes /activity (mounted at root by
// overviewRouter, not /overview/activity) with offset-based pagination via
// useInfiniteQuery so the UI can "Load more" older entries without
// re-fetching everything. First page auto-refreshes every 5s so the tab
// stays live; older pages are cached until invalidated.
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

export interface ActivityRow {
  id: number;
  grid_id: string | null;
  session_id: string | null;
  category: string;
  message: string;
  detail: string | null;
  created_at: string;
}

const PAGE_SIZE = 50;

/**
 * Category filter is passed to the backend so each page size is predictable
 * (always PAGE_SIZE matching rows, regardless of filter). Client-side
 * filtering after fetch makes page counts jumpy — 50 raw rows might yield
 * only 3 AI rows, then "Load more" loads 50 more that might yield 8, etc.
 */
export function useActivity(category?: string) {
  return useInfiniteQuery<ActivityRow[]>({
    queryKey: ["activity", category ?? "all"],
    queryFn: ({ pageParam = 0 }) => {
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(pageParam),
      });
      if (category) qs.set("category", category);
      return apiGet<ActivityRow[]>(`/activity?${qs.toString()}`);
    },
    initialPageParam: 0,
    // Next page only exists if the current page was full — a short page means
    // we've hit the tail and further requests would return [].
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_SIZE ? undefined : allPages.length * PAGE_SIZE,
    // SSE pushes activity events — every logActivity() emit invalidates this
    // query, so we only need a long safety-net poll for missed events.
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}
